import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import Model, { Providers } from "@tripian/model";
import { providers } from "@tripian/core";
import { PreLoading, KiwiHotelSearchBar, KiwiHotelCard, KiwiHotelFilterBar, KiwiHotelFilterModal, KiwiSpecialOffersModal, KiwiHotelInfoModal } from "@tripian/react";
import type { KiwiSortOption, KiwiHotelFilters, KiwiCitySuggestion } from "@tripian/react";
import useTrip from "../../../../../../hooks/useTrip";
import useReservation from "../../../../../../hooks/useReservation";
import * as gtag from "../../../../../../gtag";
import classes from "./KiwiHotelContainer.module.scss";

const buildPriceMarkerSvg = (price: string): string => {
  const textLen = price.length;
  const width = Math.max(50, textLen * 9 + 20);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="32" viewBox="0 0 ${width} 32">
    <rect x="0" y="0" width="${width}" height="28" rx="14" fill="#1a1f71"/>
    <polygon points="${width / 2 - 5},28 ${width / 2 + 5},28 ${width / 2},32" fill="#1a1f71"/>
    <text x="${width / 2}" y="18" text-anchor="middle" fill="#fff" font-size="12" font-weight="700" font-family="Arial,sans-serif">${price}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const buildInfoWindowContent = (hotel: Providers.Kiwi.KiwiSearchHotel): string => {
  const price = hotel.lowestNightRate || hotel.minimumRate;
  const priceText = price ? `$${parseFloat(price).toFixed(0)}` : "";
  const score = parseFloat(hotel.kiwiOverallScore) || 0;
  const stars = Math.round(parseFloat(hotel.starRating) || 0);
  const starsHtml = stars > 0 ? '<span style="color:#f59e0b;font-size:11px;margin-left:6px;">' + "★".repeat(stars) + "</span>" : "";
  const location = [hotel.city, hotel.country].filter(Boolean).join(", ");

  const img = hotel.mainImageUrl
    ? `<img src="${hotel.mainImageUrl}" style="width:100%;height:140px;object-fit:cover;display:block;" />`
    : `<div style="width:100%;height:140px;background:#f3f4f6;display:flex;align-items:center;justify-content:center;color:#d1d5db;">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/></svg>
      </div>`;

  const scoreBadge = score > 0
    ? `<div style="position:absolute;top:8px;left:8px;background:rgba(0,0,0,0.65);color:#fff;padding:3px 8px;border-radius:12px;font-size:11px;font-weight:700;display:flex;align-items:center;gap:3px;">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
        ${score.toFixed(1)}
      </div>`
    : "";

  const detailBtn = `<button type="button" class="kiwi-iw-detail"
        style="display:block;width:100%;text-align:center;margin-top:10px;padding:8px 0;background:#1a1f71;color:#fff;border:none;border-radius:8px;font-size:12px;font-weight:600;letter-spacing:0.3px;cursor:pointer;transition:opacity 0.15s;"
        onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
        View Details
      </button>`;

  return `<div class="kiwi-iw-card" style="width:260px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;overflow:hidden;border-radius:12px;background:#fff;cursor:pointer;">
    <div style="position:relative;">
      ${img}
      ${scoreBadge}
    </div>
    <div style="padding:12px 14px 14px;">
      <div style="display:flex;align-items:center;margin-bottom:3px;">
        <span style="font-size:14px;font-weight:700;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:180px;">${hotel.title}</span>
        ${starsHtml}
      </div>
      ${location ? `<div style="font-size:11px;color:#9ca3af;margin-bottom:8px;">${location}</div>` : ""}
      ${priceText ? `<div style="display:flex;align-items:baseline;gap:4px;">
        <span style="font-size:18px;font-weight:800;color:#1a1f71;">${priceText}</span>
        <span style="font-size:11px;color:#9ca3af;font-weight:400;">/night</span>
      </div>` : ""}
      ${detailBtn}
    </div>
  </div>`;
};

interface IKiwiHotelContainer {
  show: boolean;
  kiwiHotels: Providers.Kiwi.KiwiSearchHotel[];
  isLoading: boolean;
  loadingMore: boolean;
  hasNextPage: boolean;
  totalItems: number;
  destination: string;
  checkInDate: string;
  checkOutDate: string;
  adults: number;
  childrenCount: number;
  onDestinationChange: (value: string) => void;
  onCheckInChange: (value: string) => void;
  onCheckOutChange: (value: string) => void;
  onAdultsChange: (value: number) => void;
  onChildrenChange: (value: number) => void;
  onSearch: () => void;
  sortBy: KiwiSortOption;
  onSortChange: (sort: KiwiSortOption) => void;
  filters: KiwiHotelFilters;
  onFiltersChange: (filters: KiwiHotelFilters) => void;
  onLoadMore: () => void;
  cities?: Model.City[];
  t: (value: Model.TranslationKey) => string;
}

const KiwiHotelContainer: React.FC<IKiwiHotelContainer> = ({
  show,
  kiwiHotels,
  isLoading,
  loadingMore,
  hasNextPage,
  totalItems,
  destination,
  checkInDate,
  checkOutDate,
  adults,
  childrenCount,
  onDestinationChange,
  onCheckInChange,
  onCheckOutChange,
  onAdultsChange,
  onChildrenChange,
  onSearch,
  sortBy,
  onSortChange,
  filters,
  onFiltersChange,
  onLoadMore,
  cities,
  t,
}) => {
  const { tripReference } = useTrip();
  const { reservationAdd } = useReservation();

  const citySuggestions: KiwiCitySuggestion[] = useMemo(() => {
    if (!cities || !destination || destination.length < 2) return [];
    const search = destination.toLowerCase();
    return cities
      .filter((c) => c.name.toLowerCase().includes(search))
      .slice(0, 10)
      .map((c) => ({
        id: c.id,
        name: c.name,
        parentName: c.parentLocations.length > 0
          ? `${c.parentLocations.map((p) => p.name).join(", ")}, ${c.country.name}`
          : c.country.name,
      }));
  }, [cities, destination]);

  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [showMap, setShowMap] = useState<boolean>(false);
  const [offersModalHotel, setOffersModalHotel] = useState<string>("");
  const [specialOffers, setSpecialOffers] = useState<Providers.Kiwi.KiwiSpecialOffer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState<boolean>(false);
  const [infoModalHotel, setInfoModalHotel] = useState<Providers.Kiwi.KiwiSearchHotel | undefined>(undefined);
  const [modalDestination, setModalDestination] = useState("");
  const modalCitySuggestions: KiwiCitySuggestion[] = useMemo(() => {
    if (!cities || !modalDestination || modalDestination.length < 2) return [];
    const search = modalDestination.toLowerCase();
    return cities
      .filter((c) => c.name.toLowerCase().includes(search))
      .slice(0, 10)
      .map((c) => ({
        id: c.id,
        name: c.name,
        parentName: c.parentLocations.length > 0
          ? `${c.parentLocations.map((p) => p.name).join(", ")}, ${c.country.name}`
          : c.country.name,
      }));
  }, [cities, modalDestination]);

  const fetchPropertyDetail = useCallback(async (code: string): Promise<Providers.Kiwi.KiwiPropertyDetail> => {
    if (!providers.kiwi) throw new Error("Kiwi provider not initialized");
    const res = await providers.kiwi.getProperty(code);
    if (!res.success || !res.data) throw new Error(res.message || "Failed to fetch hotel detail");
    return res.data;
  }, []);

  const fetchPropertyAvailability = useCallback(async (
    code: string,
    request: Providers.Kiwi.KiwiPropertyAvailabilityRequest,
  ): Promise<Providers.Kiwi.KiwiPropertyAvailabilityResponse> => {
    if (!providers.kiwi) throw new Error("Kiwi provider not initialized");
    return providers.kiwi.getPropertyAvailability(code, request);
  }, []);

  const createReservation = useCallback(async (
    request: Providers.Kiwi.KiwiCreateReservationRequest,
  ): Promise<Providers.Kiwi.KiwiCreateReservationResponse> => {
    if (!providers.kiwi) throw new Error("Kiwi provider not initialized");
    return providers.kiwi.createReservation(request);
  }, []);

  const generateExternalId = useCallback((): string => {
    return `tripian-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }, []);

  const handleReservationCreated = useCallback((data: Providers.Kiwi.KiwiReservationData, externalId: string) => {
    if (!tripReference || !infoModalHotel) return;
    const bookingDateTime = data.inDate
      ? `${data.inDate} 00:00:00`
      : new Date().toISOString().replace("T", " ").slice(0, 19);
    gtag.googleAnalyticsEvent("kiwi_booking_confirmation", {
      hotel_code: infoModalHotel.code,
      hotel_name: infoModalHotel.title,
      hotel_city: infoModalHotel.city,
      confirmation_id: data.confirmationId || externalId,
    });
    reservationAdd(
      {
        key: data.confirmationId || externalId,
        tripHash: tripReference.tripHash,
        provider: "kiwi",
        value: {
          ...data,
          hotelTitle: infoModalHotel.title,
          hotelImage: infoModalHotel.mainImageUrl,
          hotelCity: infoModalHotel.city,
        },
        bookingDateTime,
      },
      false,
    );
  }, [tripReference, infoModalHotel, reservationAdd]);

  const handleSpecialOffersClick = useCallback((hotel: Providers.Kiwi.KiwiSearchHotel) => {
    if (!providers.kiwi) return;
    gtag.googleAnalyticsEvent("kiwi_special_offers_open", {
      hotel_code: hotel.code,
      hotel_name: hotel.title,
      hotel_city: hotel.city,
    });
    setOffersModalHotel(hotel.title);
    setSpecialOffers([]);
    setLoadingOffers(true);

    providers.kiwi
      .getProperty(hotel.code)
      .then((res) => {
        if (res.success && res.data?.specialOffers) {
          setSpecialOffers(res.data.specialOffers);
        }
      })
      .catch((err) => {
        console.error("Kiwi getProperty error:", err);
      })
      .finally(() => {
        setLoadingOffers(false);
      });
  }, []);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const markerMapRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const openHotelDetailRef = useRef<(hotel: Providers.Kiwi.KiwiSearchHotel) => void>(() => {});
  const hotelsRef = useRef(kiwiHotels);
  hotelsRef.current = kiwiHotels;
  const containerRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const loadingRef = useCallback(
    (node: HTMLDivElement) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [onLoadMore]
  );

  const clearMarkers = useCallback(() => {
    if (infoWindowRef.current) infoWindowRef.current.close();
    markersRef.current.forEach((m) => {
      google.maps.event.clearInstanceListeners(m);
      m.setMap(null);
    });
    markersRef.current = [];
    markerMapRef.current.clear();
  }, []);

  const addMarkers = useCallback(() => {
    clearMarkers();
    const map = window.twindow.map;
    if (!map || hotelsRef.current.length === 0) return;

    if (infoWindowRef.current) infoWindowRef.current.close();
    infoWindowRef.current = new google.maps.InfoWindow({ maxWidth: 280, pixelOffset: new google.maps.Size(0, -4) });

    const bounds = new google.maps.LatLngBounds();

    hotelsRef.current.forEach((hotel) => {
      const lat = parseFloat(hotel.latitude);
      const lng = parseFloat(hotel.longitude);
      if (!lat || !lng) return;

      const price = hotel.lowestNightRate || hotel.minimumRate;
      const priceLabel = price ? `$${parseFloat(price).toFixed(0)}` : "Hotel";
      const svgUrl = buildPriceMarkerSvg(priceLabel);
      const textLen = priceLabel.length;
      const iconWidth = Math.max(50, textLen * 9 + 20);

      const marker = new google.maps.Marker({
        position: { lat, lng },
        map,
        icon: {
          url: svgUrl,
          scaledSize: new google.maps.Size(iconWidth, 32),
          anchor: new google.maps.Point(iconWidth / 2, 32),
        },
        title: hotel.title,
      });

      marker.addListener("click", () => {
        if (infoWindowRef.current) {
          infoWindowRef.current.setContent(buildInfoWindowContent(hotel));
          infoWindowRef.current.open(map, marker);

          // Remove default InfoWindow styling
          google.maps.event.addListenerOnce(infoWindowRef.current, "domready", () => {
            // Whole card (and its View Details button) opens the in-app hotel detail modal
            const card = document.querySelector(".kiwi-iw-card") as HTMLElement | null;
            if (card) {
              card.addEventListener("click", (e) => {
                e.stopPropagation();
                openHotelDetailRef.current(hotel);
              });
            }
            const iwOuter = document.querySelector(".gm-style-iw-c") as HTMLElement;
            if (iwOuter) {
              iwOuter.style.padding = "0";
              iwOuter.style.borderRadius = "12px";
              iwOuter.style.boxShadow = "0 6px 24px rgba(0,0,0,0.18)";
              iwOuter.style.overflow = "hidden";
            }
            const iwBackground = document.querySelector(".gm-style-iw-d") as HTMLElement;
            if (iwBackground) {
              iwBackground.style.overflow = "hidden";
              iwBackground.style.padding = "0";
            }
            const iwHeader = document.querySelector(".gm-style-iw-ch") as HTMLElement;
            if (iwHeader) {
              iwHeader.style.paddingTop = "0";
            }
            // Hide default close button, add custom one
            const closeBtn = document.querySelector(".gm-ui-hover-effect") as HTMLElement;
            if (closeBtn) {
              closeBtn.style.display = "none";
            }
            const iwOuter2 = document.querySelector(".gm-style-iw-c") as HTMLElement;
            if (iwOuter2 && !iwOuter2.querySelector(".kiwi-iw-close")) {
              const customClose = document.createElement("button");
              customClose.className = "kiwi-iw-close";
              customClose.innerHTML = "✕";
              customClose.style.cssText = "position:absolute;top:8px;right:8px;z-index:10;background:rgba(0,0,0,0.55);border:none;border-radius:50%;width:26px;height:26px;color:#fff;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;line-height:1;";
              customClose.addEventListener("click", () => {
                if (infoWindowRef.current) infoWindowRef.current.close();
              });
              iwOuter2.appendChild(customClose);
            }
          });
        }
      });

      markersRef.current.push(marker);
      markerMapRef.current.set(hotel.code, marker);
      bounds.extend({ lat, lng });
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 50);
    }
  }, [clearMarkers]);

  const handleHotelHover = useCallback((hotelCode: string | null) => {
    if (!showMap) return;
    markerMapRef.current.forEach((m) => m.setAnimation(null));
    if (hotelCode) {
      const marker = markerMapRef.current.get(hotelCode);
      if (marker) {
        marker.setAnimation(google.maps.Animation.BOUNCE);
      }
    }
  }, [showMap]);

  // Toggle markers when showMap changes
  useEffect(() => {
    if (showMap) {
      addMarkers();
    } else {
      clearMarkers();
    }
  }, [showMap, addMarkers, clearMarkers]);

  // Refresh markers when hotels change (new search) while map is on
  useEffect(() => {
    if (showMap) {
      addMarkers();
    }
  }, [kiwiHotels]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear markers when container hides
  useEffect(() => {
    if (!show) {
      clearMarkers();
      setShowMap(false);
    }
  }, [show, clearMarkers]);

  const handleHotelCardClick = useCallback((hotel: Providers.Kiwi.KiwiSearchHotel) => {
    gtag.googleAnalyticsEvent("kiwi_hotel_detail_open", {
      hotel_code: hotel.code,
      hotel_name: hotel.title,
      hotel_city: hotel.city,
      price: hotel.lowestNightRate || hotel.minimumRate,
      currency: hotel.currencyCode,
    });
    setInfoModalHotel(hotel);
  }, []);
  openHotelDetailRef.current = handleHotelCardClick;

  const handleSearch = useCallback(() => {
    gtag.googleAnalyticsEvent("kiwi_hotel_search", {
      destination,
      check_in: checkInDate,
      check_out: checkOutDate,
      adults,
      children: childrenCount,
    });
    onSearch();
  }, [destination, checkInDate, checkOutDate, adults, childrenCount, onSearch]);

  const handleSortChange = useCallback((sort: KiwiSortOption) => {
    gtag.googleAnalyticsEvent("kiwi_sort_change", { sort_by: sort });
    onSortChange(sort);
  }, [onSortChange]);

  const handleFiltersChange = useCallback((newFilters: KiwiHotelFilters) => {
    const activeCount = Object.values(newFilters).filter((v) => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== false && v !== null;
    }).length;
    gtag.googleAnalyticsEvent("kiwi_filter_apply", { filter_count: activeCount });
    onFiltersChange(newFilters);
  }, [onFiltersChange]);

  const handleMapToggle = useCallback(() => {
    const next = !showMap;
    gtag.googleAnalyticsEvent("kiwi_map_toggle", { show_map: next });
    setShowMap(next);
  }, [showMap]);

  const handleRoomsTabView = useCallback((h: Providers.Kiwi.KiwiSearchHotel) => {
    gtag.googleAnalyticsEvent("kiwi_rooms_tab_view", {
      hotel_code: h.code,
      hotel_name: h.title,
    });
  }, []);

  const handleRoomAvailabilitySearch = useCallback((h: Providers.Kiwi.KiwiSearchHotel, checkIn: string, checkOut: string, adults_: number, children_: number) => {
    gtag.googleAnalyticsEvent("kiwi_room_availability_search", {
      hotel_code: h.code,
      hotel_name: h.title,
      check_in: checkIn,
      check_out: checkOut,
      adults: adults_,
      children: children_,
    });
  }, []);

  const handleRateDetailOpen = useCallback((h: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate) => {
    gtag.googleAnalyticsEvent("kiwi_rate_detail_open", {
      hotel_code: h.code,
      hotel_name: h.title,
      room_title: room.title,
      rate_code: rate.code,
    });
  }, []);

  const handleSeeMoreDetails = useCallback((h: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate) => {
    gtag.googleAnalyticsEvent("kiwi_see_more_details", {
      hotel_code: h.code,
      hotel_name: h.title,
      room_title: room.title,
      rate_code: rate.code,
    });
  }, []);

  const handleBookNowClick = useCallback((h: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate) => {
    gtag.googleAnalyticsEvent("kiwi_book_now_click", {
      hotel_code: h.code,
      hotel_name: h.title,
      hotel_city: h.city,
      room_title: room.title,
      rate_code: rate.code,
      price: rate.price?.inclusiveTotal,
      currency: rate.price?.currencyCode,
    });
  }, []);

  const handleReservationFailed = useCallback((h: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate, errorMessage: string) => {
    gtag.googleAnalyticsEvent("kiwi_booking_failed", {
      hotel_code: h.code,
      hotel_name: h.title,
      hotel_city: h.city,
      room_title: room.title,
      rate_code: rate.code,
      error_message: errorMessage,
    });
  }, []);

  const containerClasses = [classes.kiwiHotelContainer];
  if (!window.tconfig.SHOW_OVERVIEW)
    containerClasses.push("trip-layout-container-height");
  else containerClasses.push("trip-layout-container-height-tab");
  if (show) containerClasses.push(classes.containerOpen);
  else containerClasses.push(classes.containerClose);

  if (!show) {
    return null;
  }

  return (
    <div className={containerClasses.join(" ")}>
      <div className="flex flex-col h-full mb-16 md:mb-0">
        {/* Search Bar */}
        <div className="py-4">
          <KiwiHotelSearchBar
            destination={destination}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            adults={adults}
            children={childrenCount}
            citySuggestions={citySuggestions}
            onDestinationChange={onDestinationChange}
            onCitySelect={(city) => onDestinationChange(city.name)}
            onCheckInChange={onCheckInChange}
            onCheckOutChange={onCheckOutChange}
            onAdultsChange={onAdultsChange}
            onChildrenChange={onChildrenChange}
            onSearch={handleSearch}
            t={t}
          />
        </div>

        {/* Filter Bar */}
        <KiwiHotelFilterBar
          sortBy={sortBy}
          onSortChange={handleSortChange}
          onShowMapToggle={handleMapToggle}
          showMap={showMap}
          onFilterToggle={() => setFilterOpen(prev => !prev)}
          resultCount={totalItems}
          t={t}
        />

        {/* Results */}
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <div className="flex flex-col items-center justify-center">
              <PreLoading />
            </div>
          </div>
        ) : kiwiHotels.length === 0 ? (
          <div className="flex flex-col h-full items-center justify-center mt-10 text-gray-500">
            {t("trips.myTrips.localExperiences.toursEmpty")}
          </div>
        ) : (
          <div ref={containerRef} className="flex-1 overflow-auto py-4">
            <div className="flex flex-col gap-4">
              {kiwiHotels.map(hotel => (
                <KiwiHotelCard
                  key={hotel.code}
                  hotel={hotel}
                  showVisaBadge={true}
                  onHover={handleHotelHover}
                  onSpecialOffersClick={handleSpecialOffersClick}
                  onCardClick={handleHotelCardClick}
                  checkIn={checkInDate}
                  checkOut={checkOutDate}
                  adults={adults}
                  children={childrenCount}
                  t={t}
                />
              ))}
              {hasNextPage && (
                <div
                  ref={loadingRef}
                  className="h-10 flex items-center justify-center"
                >
                  {loadingMore && <PreLoading size="small" />}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Filter Modal */}
      {filterOpen && (
        <KiwiHotelFilterModal
          hotels={kiwiHotels}
          filters={filters}
          onApply={(newFilters) => {
            handleFiltersChange(newFilters);
            setFilterOpen(false);
          }}
          onClose={() => setFilterOpen(false)}
          t={t}
        />
      )}

      {/* Special Offers Modal */}
      {offersModalHotel && (
        <KiwiSpecialOffersModal
          hotelName={offersModalHotel}
          specialOffers={loadingOffers ? undefined : specialOffers}
          onClose={() => setOffersModalHotel("")}
          t={t}
        />
      )}

      {/* Hotel Info Modal */}
      {infoModalHotel && (
        <KiwiHotelInfoModal
          hotel={infoModalHotel}
          onClose={() => setInfoModalHotel(undefined)}
          fetchPropertyDetail={fetchPropertyDetail}
          fetchPropertyAvailability={fetchPropertyAvailability}
          availabilityRequest={{
            in_date: checkInDate,
            out_date: checkOutDate,
            adults,
            children: childrenCount,
          }}
          searchDestination={destination}
          searchCheckIn={checkInDate}
          searchCheckOut={checkOutDate}
          searchAdults={adults}
          searchChildren={childrenCount}
          searchCitySuggestions={modalCitySuggestions}
          onSearchDestinationChange={setModalDestination}
          onSearchCitySelect={(city) => setModalDestination(city.name)}
          onCreateReservation={createReservation}
          onReservationCreated={handleReservationCreated}
          generateExternalId={generateExternalId}
          showVisaBadge={true}
          dayBadgeText="Day 14"
          topOffset="90px"
          onRoomsTabView={handleRoomsTabView}
          onRoomAvailabilitySearch={handleRoomAvailabilitySearch}
          onRateDetailOpen={handleRateDetailOpen}
          onSeeMoreDetails={handleSeeMoreDetails}
          onBookNowClick={handleBookNowClick}
          onReservationFailed={handleReservationFailed}
          t={t}
        />
      )}
    </div>
  );
};

export default KiwiHotelContainer;